import { findScheduledAlerts, type UserAlertRow } from '@api/userAlerts';
import { Button, type ButtonProps } from '@mantine/core';
import { CalendarCheck, CalendarPlus } from '@phosphor-icons/react';
import { useCallback, useEffect, useState, type FC } from 'react';

import ScheduleAlertDialog, { type ScheduleAlertDialogProps } from './ScheduleAlertDialog';
import { frequencyLabel } from './schedule.utils';

type Existing = UserAlertRow & { parsedContext: Record<string, unknown> };

type Props = Omit<ScheduleAlertDialogProps, 'opened' | 'onClose' | 'existing' | 'onChanged'> & {
    /** The context key that names this artifact (objectiveId, channelKey, scopeKey): same value = same schedule. */
    matchOn: string;
    size?: ButtonProps['size'];
    variant?: ButtonProps['variant'];
    color?: ButtonProps['color'];
    radius?: ButtonProps['radius'];
    /** Extra classes for layouts that stretch the button. */
    className?: string;
};

/**
 * The planner's Schedule button. It reads whether this artifact is already scheduled: if so it
 * says when ("Scheduled · daily 07:00 · auto-save") and opens that schedule to edit or remove;
 * if not it creates one. One schedule per artifact, edited where it was made.
 */
const ScheduleButton: FC<Props> = ({ matchOn, size = 'xs', variant = 'default', color, radius, className, ...dialog }) => {
    const [open, setOpen] = useState(false);
    const [existing, setExisting] = useState<Existing | null>(null);
    const key = dialog.alert.context[matchOn];
    const load = useCallback(async () => {
        try {
            const rows = await findScheduledAlerts((ctx) => ctx[matchOn] === key);
            setExisting(rows.find((r) => r.alertType === dialog.alert.alertType) ?? null);
        } catch {
            setExisting(null);
        }
    }, [matchOn, key, dialog.alert.alertType]);
    useEffect(() => {
        void load();
    }, [load]);
    const autoSaveOn = existing?.parsedContext.autoSave === true || existing?.parsedContext.autoSave === 'true';
    return (
        <>
            <Button size={size} variant={existing ? 'light' : variant} color={existing ? 'grape' : color} radius={radius} className={className} leftSection={existing ? <CalendarCheck size={14} /> : <CalendarPlus size={14} />} onClick={() => setOpen(true)} title={existing ? 'Edit or remove this schedule' : undefined}>
                {existing ? `Scheduled · ${frequencyLabel(existing.frequency, existing.triggerTime, existing.triggerDayOfWeek)}${dialog.autoSave && autoSaveOn ? ' · auto-save' : ''}` : 'Schedule'}
            </Button>
            <ScheduleAlertDialog {...dialog} title={existing ? dialog.title.replace(/^Schedule:/, 'Scheduled:') : dialog.title} opened={open} onClose={() => setOpen(false)} existing={existing ?? undefined} onChanged={() => void load()} />
        </>
    );
};

export default ScheduleButton;
